


import React, { useState, useEffect } from 'react';
import { Bell, Lock, AlertTriangle, Info, AlertCircle, X, Megaphone } from 'lucide-react';
import { db } from '../services/db';
import { SchoolData, Announcement } from '../types';
import { cn } from '../utils/cn';

export const GlobalBanner = () => {
  const [banner, setBanner] = useState<Announcement | null>(null);
  const [school, setSchool] = useState<SchoolData | null>(null);
  const [visible, setVisible] = useState(true);

  const fetchData = async () => {
    const user = await db.getCurrentUser();
    
    // In a real app, we'd fetch the latest *active* system broadcast
    const announcements = await db.getAnnouncements('all'); 
    
    // Filter for system type, is_banner, and target_role
    const activeSystem = announcements
        .filter(a => a.type === 'system' && a.is_banner && (!user ? a.target_role === 'all' : (a.target_role === 'all' || a.target_role === user.role)))
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0];
    
    if (activeSystem) {
        if (!user) {
            // Force banner to be treated as high priority (amber/yellow) for public view
            setBanner({ ...activeSystem, priority: 'high' });
        } else {
            setBanner(activeSystem);
        }
        setVisible(true);
    } else {
        setBanner(null);
    }

    if (user && user.school_id) {
       const s = await db.getSchool(user.school_id);
       setSchool(s);
    }
  };

  useEffect(() => {
    fetchData();
    const handleUpdate = () => fetchData();
    window.addEventListener('global-banner-change', handleUpdate);
    return () => window.removeEventListener('global-banner-change', handleUpdate);
  }, []);

  // Priority 1: School Inactive (Locked) - Non-dismissible
  if (school && school.status === 'inactive') {
     return (
        <div className="bg-red-600 text-white px-4 py-3 relative z-[100] shadow-md animate-in slide-in-from-top duration-300">
           <div className="max-w-7xl mx-auto flex items-center justify-center gap-3">
              <Lock className="shrink-0 fill-red-700 text-white" size={18} />
              <p className="text-sm font-black uppercase tracking-widest leading-tight">
                 System Read-Only: Academic Year Closed
              </p>
           </div>
        </div>
     );
  }

  // Priority 2: Custom System Broadcast
  if (banner && visible) {
    const priority = banner.priority || 'normal';
    
    // Theme Configuration
    const styles = {
        critical: 'bg-red-500 text-white border-b border-red-600',
        high: 'bg-[#fbc02d] text-[#333333] border-b border-[#e0a800]', // Professional yellow banner
        normal: 'bg-blue-600 text-white border-b border-blue-700'
    };

    const icons = {
        critical: <AlertCircle size={20} className="fill-white/20"/>,
        high: <Bell size={18} className="fill-[#8a6d3b] text-[#8a6d3b]"/>,
        normal: <Megaphone size={20} className="fill-white/20"/>
    };


    // Extract link if it exists in the content (basic parsing) or use action_link if added to type
    // For this implementation, we'll check if the content has a URL or we'll just render it as is.
    // We'll also support an explicit action_link and action_text if they exist on the banner object.
    const actionLink = banner.action_link;
    const actionText = banner.action_text || 'TO OPEN IT, CLICK HERE.';

    return (

      <div className={cn("px-4 py-2.5 relative z-[100] shadow-sm transition-all duration-300 animate-in slide-in-from-top", styles[priority as keyof typeof styles])}>
        <div className="max-w-7xl mx-auto flex items-start md:items-center justify-center gap-4 relative">
          <div className="flex items-center gap-2 text-[15px]">
             <div className="shrink-0 flex items-center justify-center">
                 {icons[priority as keyof typeof icons]}
             </div>
             <div className="flex flex-wrap items-center gap-1">
                 <span className="font-bold text-[#8a6d3b]">{banner.title}</span>
                 <span className="font-normal text-[#333333]">{banner.content}</span>
                 {actionLink && (
                     <a href={actionLink} target="_blank" rel="noopener noreferrer" className="text-[#0056b3] hover:underline font-medium ml-1">
                         {actionText}
                     </a>
                 )}
             </div>
          </div>
          {priority !== 'critical' && (
              <button 
                onClick={() => setVisible(false)} 
                className="absolute right-0 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-black/5 transition-colors shrink-0 text-[#8a6d3b]"
                aria-label="Dismiss banner"
              >
                <X size={16} />
              </button>
          )}
        </div>
      </div>
    );
  }

  return null;
};


